// Item 2 — the bottom-context readout (DEMO only). Everything else lives up top
// (logo, mute, go-inside), so the screen read top-heavy. This anchors a low zone
// — the lower third where the eye rests — carrying the ambient state the visitor
// cares about: the TIME of day, the ERA, and the cassette n/N progress (item 4).
// It shares the lower band with the existing contextual pills ("sit and listen"
// / "get out" — storyDirector / item 6), so the bottom reads as one controlled
// strip rather than scattered chrome.
//
// Pure readout — pointer-events:none, write-on-change (like the dev HUD, PF6).
// It occupies the same bottom slot the demoTimeline reward chrome takes on RETURN,
// so it HIDES when the reward unlocks (the timeline's scrub + era toggle then own
// time/era, and the loop — hence n/N — is complete). John tunes content/layout.

export interface BottomContextDeps {
  /** The scene clock — `formatHM()` gives the "HH:MM" the dev time chip uses. */
  clock: { formatHM: () => string };
  /** Era now (past/present), derived from the morph (same source as the card). */
  currentEra: () => 'past' | 'present';
  /** Item 4 — distinct cassettes heard / the set size N. */
  cassetteProgress: () => { played: number; total: number };
}

export interface BottomContext {
  /** Poll + repaint (write-on-change). Ticked from the render loop. */
  update(): void;
  /** Reveal/hide the strip (hidden on RETURN when the reward chrome takes the slot). */
  hide(): void;
  dispose(): void;
}

export function createBottomContext(deps: BottomContextDeps): BottomContext {
  const bar = document.createElement('div');
  bar.style.cssText = [
    'position: fixed',
    'bottom: 22px',
    'left: 50%',
    'transform: translateX(-50%)',
    'display: flex',
    'align-items: center',
    'gap: 12px',
    'padding: 7px 16px',
    // Legibility (John, 2026-06-29: brighter text, hard to see): bright text on
    // darker glass, matching the demo pills — reads over any scene.
    'background: rgba(20, 26, 38, 0.60)',
    'border: 1px solid rgba(150, 178, 228, 0.40)',
    'border-radius: 14px',
    'backdrop-filter: blur(6px)',
    '-webkit-backdrop-filter: blur(6px)',
    'color: #eef3fb',
    'text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5)',
    'font: 600 13px/1.2 system-ui, sans-serif',
    'letter-spacing: 0.2px',
    'white-space: nowrap',
    'z-index: 1000',
    'user-select: none',
    'pointer-events: none', // a readout, not a control — never eats a click
    'opacity: 0',           // fade in on the first update (avoids a boot flash)
    'transition: opacity 320ms ease',
  ].join('; ');

  // Segments + hairline separators so the three readouts read distinctly.
  const seg = (): HTMLElement => {
    const s = document.createElement('span');
    s.style.cssText = 'display: inline-flex; align-items: center; gap: 6px;';
    return s;
  };
  const divider = (): HTMLElement => {
    const d = document.createElement('span');
    d.textContent = '·';
    d.style.cssText = 'opacity: 0.45;';
    return d;
  };
  const dim = (label: string): HTMLElement => {
    const l = document.createElement('span');
    l.textContent = label;
    l.style.cssText = 'opacity: 0.6; font-size: 11px;';
    return l;
  };

  const timeSeg = seg();
  const timeVal = document.createElement('span');
  timeVal.style.cssText = 'font-variant-numeric: tabular-nums; font-weight: 600;';
  timeSeg.append(timeVal); // no clock emoji — the HH:MM reads as the time on its own

  const eraSeg = seg();
  const eraVal = document.createElement('span');
  eraVal.style.cssText = 'text-transform: capitalize;';
  eraSeg.append(eraVal);

  const tapeSeg = seg();
  const tapeVal = document.createElement('span');
  tapeVal.style.cssText = 'font-variant-numeric: tabular-nums;';
  tapeSeg.append(dim('cassettes'), tapeVal);

  bar.append(timeSeg, divider(), eraSeg, divider(), tapeSeg);
  document.body.appendChild(bar);

  let shown = false;     // gated by hide() (the reward hides it)
  let hidden = false;
  let last = '';
  const update = (): void => {
    if (hidden) return;
    const hm = deps.clock.formatHM();
    const era = deps.currentEra();
    const { played, total } = deps.cassetteProgress();
    const key = `${hm}|${era}|${played}/${total}`;
    if (key === last) return; // write-on-change
    last = key;
    timeVal.textContent = hm;
    eraVal.textContent = era;
    tapeVal.textContent = `${played}/${total}`;
    if (!shown) { shown = true; bar.style.opacity = '1'; } // fade in once populated
  };
  update();

  return {
    update,
    hide(): void {
      hidden = true;
      bar.style.opacity = '0';
      bar.style.pointerEvents = 'none';
    },
    dispose(): void { bar.remove(); },
  };
}
